"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const react_2 = require("@xstate/react");
const styled_1 = __importDefault(require("@emotion/styled"));
const react_3 = require("@emotion/react");
const loaders_and_spinners_1 = require("@kyruus/loaders-and-spinners");
const machine_1 = require("../../lib/xstate/machine");
const states_1 = require("../../lib/xstate/states");
const events_1 = require("../../lib/xstate/events");
const Input_1 = require("./Input");
const Button_1 = require("./Button");
const ExternalLink_1 = require("./ExternalLink");
const EmptySearchError_1 = require("./EmptySearchError");
const Error_1 = require("./../Error");
const QuickSearch_1 = require("./QuickSearch");
const types_1 = require("../../types");
const api_1 = require("../../lib/api");
const useBreakpoints_1 = require("../../lib/utils/useBreakpoints");
const constants_1 = require("../../lib/constants");
const SearchWidgetContainer = (0, styled_1.default)('div') `
  *,
  *:before,
  *:after {
    box-sizing: border-box;
  }

  margin: 0;
  padding: 0;
  font-size: ${(props) => props.theme.sw_font_size};
  color: ${(props) => props.theme.sw_color_text};

  // template comes before css, so customers can override stuff
  ${(props) => props.template}
  ${(props) => props.css}
`;
const isLocaleInputEnabled = (params) => params.show_location;
const serializeForm = (form) => {
    const { searchTerm, location } = form;
    return {
        searchTerm: searchTerm ? searchTerm.value : '',
        location: location ? location.value : ''
    };
};
const isConfigDefined = (config) => {
    return !!config;
};
const SearchWidget = ({ id, pmcUrl, config: configFromProps, log, onSelect, onSubmit, onInputFocus, useV8, useLegacyConfig, typeaheadTerms: typeaheadTermsFromProps, location: locationFromProps, productName = 'pmc', options = {} }) => {
    const formRef = (0, react_1.useRef)(null);
    const { ref: resizeRef, breakpoint } = (0, useBreakpoints_1.useBreakpoints)();
    // Memoize the machine config.
    // xstate doesn't currently support recreating machines,
    // so we don't need to re-run the hook (nor do we want to,
    // as that may introduce unexpected behavior should xstate
    // change).
    // The value on mount will be sufficient.
    const machine = (0, react_1.useMemo)(() => (0, machine_1.createSearchWidgetMachine)({
        id,
        pmcUrl,
        serverConfig: configFromProps,
        log,
        useV8,
        useLegacyConfig,
        typeaheadTerms: typeaheadTermsFromProps,
        location: locationFromProps,
        productName
    }, options), []);
    const { previewMode, typeaheadOpen, noScrollDropdownResults, shouldUseContainerQuery = true, overrideBreakpoint, showEmptySearchError } = options;
    const [state, send] = (0, react_2.useMachine)(machine);
    const { config, typeaheadTerms, typeaheadSuggestions, location: searchLocation, log: track, apiResponseTime } = state.context;
    (0, react_1.useEffect)(() => {
        send({
            type: events_1.Events.INITIALIZE_STATE,
            data: {
                typeaheadTerms: typeaheadTermsFromProps,
                location: locationFromProps
            }
        });
    }, [send, typeaheadTermsFromProps, locationFromProps]);
    (0, react_1.useEffect)(() => {
        if (previewMode) {
            // toggle typeahead open/closed
            if (typeaheadOpen) {
                send({
                    type: events_1.Events.OPEN_TYPEAHEAD
                });
            }
            else {
                send({
                    type: events_1.Events.CLOSE_TYPEAHEAD
                });
            }
            // toggle error show/hide
            if (showEmptySearchError) {
                send({
                    type: events_1.Events.PREVIEW_SHOW_ERROR
                });
            }
            else {
                send({
                    type: events_1.Events.PREVIEW_HIDE_ERROR
                });
            }
        }
    }, [send, previewMode, typeaheadOpen, showEmptySearchError]);
    const handleOnChange = (0, react_1.useCallback)((_event, data) => {
        send({
            type: events_1.Events.SEND_TYPEAHEAD_TERMS,
            data
        });
    }, [send]);
    const handleOnLocationChange = (0, react_1.useCallback)((_event, data) => {
        send({
            type: events_1.Events.SEND_LOCATION,
            data
        });
    }, [send]);
    const handleSelect = (0, react_1.useCallback)((_event, suggestion, reason) => {
        // Although we should never reach this point without `config` being defined,
        // TypeScript has no way of knowing that at this point.
        // We'll use the type guard here that assures the TypeScript compiler that
        // `config` will be defined when the inner code is executed.
        if (isConfigDefined(config)) {
            if (!suggestion) {
                // change was triggered by `clear` event
                return;
            }
            switch (reason) {
                case 'selectOption': {
                    // user selected an option from the suggestions dropdown
                    if (suggestion && (0, types_1.isFormattedSuggestion)(suggestion)) {
                        // track user clicked suggestion
                        if (track) {
                            track(constants_1.SUGGESTION_CLICKED, {
                                search_query: typeaheadTerms,
                                suggestion_results: typeaheadSuggestions,
                                suggestion: suggestion.highlight,
                                category: suggestion.category,
                                position: typeaheadSuggestions === null || typeaheadSuggestions === void 0 ? void 0 : typeaheadSuggestions.indexOf(suggestion),
                                total_suggestions: typeaheadSuggestions === null || typeaheadSuggestions === void 0 ? void 0 : typeaheadSuggestions.length,
                                response_time: apiResponseTime
                            });
                        }
                        let queryString = suggestion.queryString;
                        let location = '';
                        if (formRef.current && formRef.current.location) {
                            location = formRef.current.location.value.trim();
                        }
                        if (location) {
                            queryString += `&${(0, api_1.buildLocationQuery)(location, config.params.location_radius)}`;
                        }
                        if (onSelect) {
                            onSelect(queryString);
                        }
                        else {
                            if (config.params.open_results_in_new_tab) {
                                window.open(queryString, '_blank');
                            }
                            else {
                                window.location.href = queryString;
                            }
                        }
                    }
                    return;
                }
                // |'create-option'
                // |'remove-option'
                // |'clear'
                // |'blur';
                default: {
                    return;
                }
            }
        }
    }, [
        config,
        onSelect,
        track,
        typeaheadSuggestions,
        typeaheadTerms,
        apiResponseTime
    ]);
    const handleSubmit = (event) => {
        event.preventDefault();
        // Although we should never reach this point without `config` being defined,
        // TypeScript has no way of knowing that at this point.
        // We'll use the type guard here that assures the TypeScript compiler that
        // `config` will be defined when the inner code is executed.
        if (isConfigDefined(config)) {
            send({ type: events_1.Events.SUBMIT_FORM });
            const { searchTerm, location } = serializeForm(event.currentTarget);
            if (config.params.allow_empty_search || searchTerm) {
                let path = useV8
                    ? (0, api_1.buildUnifiedSearchURLV8)(config, searchTerm, pmcUrl)
                    : (0, api_1.buildUnifiedSearchURL)(config, searchTerm, pmcUrl);
                if (location) {
                    path += `&${(0, api_1.buildLocationQuery)(location, config.params.location_radius)}`;
                }
                if (onSubmit) {
                    onSubmit(path);
                }
                else {
                    if (config.params.open_results_in_new_tab) {
                        window.open(path, '_blank');
                    }
                    else {
                        window.location.href = path;
                    }
                }
            }
        }
    };
    const handleOpen = () => {
        send({ type: events_1.Events.OPEN_TYPEAHEAD });
    };
    const handleClose = () => {
        send({ type: events_1.Events.CLOSE_TYPEAHEAD });
    };
    if (state.matches(`${states_1.States.INITIALIZING}.${states_1.States.ERROR}`)) {
        return react_1.default.createElement(Error_1.InitializationFailure, null);
    }
    if (state.matches(`${states_1.States.INITIALIZING}`)) {
        return (
        // TODO do we want to override the defaults from UI theme?
        react_1.default.createElement(react_3.ThemeProvider, { theme: {} },
            react_1.default.createElement(loaders_and_spinners_1.ShimmeringLoaderBar, { height: 95 })));
    }
    // shouldn't get here due to the state machine - TS won't trust it, though
    if (!isConfigDefined(config)) {
        return null;
    }
    const { template, params, display_dictionary, theme, css } = config;
    return (react_1.default.createElement(react_3.ThemeProvider, { theme: theme },
        react_1.default.createElement(SearchWidgetContainer
        // `data-breakpoint` gets set using a `reziseObserver` to mimic `container queries`
        // So if the container of this widget is less than width X, the breakpoint gets set to `sm`
        //
        // During server side rendering, this breaks because a snapshot is taken before we
        // can adjust the size. So, we want to use media queries for that
        //
        // Because of this, the css we'll have:
        // .__mq__ {
        //    @media (min-width: ${BREAKPOINTS.md}px)
        //  }
        //
        // .__cq__[data-breakpoint=md] {
        //   ...
        // }
        , { 
            // `data-breakpoint` gets set using a `reziseObserver` to mimic `container queries`
            // So if the container of this widget is less than width X, the breakpoint gets set to `sm`
            //
            // During server side rendering, this breaks because a snapshot is taken before we
            // can adjust the size. So, we want to use media queries for that
            //
            // Because of this, the css we'll have:
            // .__mq__ {
            //    @media (min-width: ${BREAKPOINTS.md}px)
            //  }
            //
            // .__cq__[data-breakpoint=md] {
            //   ...
            // }
            className: shouldUseContainerQuery ? '__cq__' : '__mq__', "data-breakpoint": overrideBreakpoint !== null && overrideBreakpoint !== void 0 ? overrideBreakpoint : breakpoint, "data-ref": "container", ref: resizeRef, css: css, template: template.container, theme: theme },
            react_1.default.createElement("form", { ref: formRef, onSubmit: handleSubmit },
                react_1.default.createElement("div", { "data-ref": "form" },
                    react_1.default.createElement(Input_1.FreeTextInput, { id: process.env.NODE_ENV === 'test' ? 'INPUT_TESTID' : undefined, searchLabelTemplate: template.searchLabel, searchInputTemplate: template.searchInput, autoComplete: template.autoComplete, onChange: handleOnChange, onSelect: handleSelect, onInputFocus: onInputFocus, value: typeaheadTerms, suggestions: typeaheadSuggestions, displayDictionary: display_dictionary, onOpen: handleOpen, onClose: handleClose, state: state, noScrollDropdownResults: noScrollDropdownResults }),
                    (state.matches(`${states_1.States.EMPTY_SEARCH_ERROR}`) ||
                        state.matches(`${states_1.States.PREVIEW}.error.${states_1.States.EMPTY_SEARCH_ERROR}`)) && (react_1.default.createElement(EmptySearchError_1.EmptySearchError, { config: config, template: template.emptySearchError })),
                    isLocaleInputEnabled(params) && (react_1.default.createElement(Input_1.LocaleInput, { localeLabelTemplate: template.localeLabel, localeInputTemplate: template.localeInput, displayDictionary: display_dictionary, value: searchLocation, onChange: handleOnLocationChange })),
                    react_1.default.createElement(Button_1.Button, { template: template.button, displayDictionary: display_dictionary }),
                    react_1.default.createElement(ExternalLink_1.ExternalLink, { template: template.extLink, config: config }),
                    react_1.default.createElement(QuickSearch_1.QuickSearch, { quickSearch: config.add_ons.quick_search }))))));
};
exports.default = SearchWidget;
//# sourceMappingURL=SearchWidget.js.map